import React, { useEffect, useRef, useState } from "react";

import { Box } from "@mui/material";
import { File } from "Models/file";
import { useTranslation } from "react-i18next";

import { TWO_MB } from "../../../constants";
import { fileIsValid } from "../../../util/fileIsValid";
import ChangeImageButton from "./ChangeImageButton";
import {
  Container,
  FileInputBox,
  FileInputSubText,
  FileInputText,
  ImageSelected,
  InputImage,
  LabelImage,
  LabelText,
} from "./styles";

interface UploadImageProps {
  labelText?: string;
  onChangePicture?: (value?: string) => void;
  picture?: string;
  maxSize?: number;
  allowedImageExtensions?: string[];
  disabled?: boolean;
  onRemovePicture?: () => void;
  containerGap?: string;
}

export const DEFAULT_ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif"];

export const UploadImageFileInput = ({
  labelText,
  picture,
  maxSize = TWO_MB,
  allowedImageExtensions = DEFAULT_ALLOWED_IMAGE_EXTENSIONS,
  disabled,
  onRemovePicture,
  onChangePicture,
  containerGap,
}: UploadImageProps) => {
  const [filePath, setFilePath] = useState<string>("");

  const { t: translate } = useTranslation();
  const imgRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (picture) setFilePath(picture && picture.includes("https://") ? picture : filePath);
    else setFilePath("");
  }, [picture]);

  const imageIsValid = (image?: File) => {
    return fileIsValid(translate, image, {
      allowedExtensions: allowedImageExtensions,
      maxSize,
    });
  };

  const handleChangeImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const image = event?.target?.files[0];

      const type = image.type.split("/");
      const extension = type.length > 0 ? type[1] : image.type;

      const fileToSave = {
        extension,
        size: image.size,
      } as File;

      if (image && imageIsValid(fileToSave)) {
        const reader = new FileReader();

        reader.onloadend = () => {
          setFilePath(reader.result ? reader.result?.toString() : "");
          const imageBase64 = reader.result?.toString().split(",");

          if (onChangePicture && imageBase64 && imageBase64?.length > 0)
            onChangePicture(imageBase64[1]);
        };
        reader.onerror = () => {
          setFilePath("");
        };
        reader.readAsDataURL(image);
      }
    }

    event.target.value = "";
  };

  const clearFile = () => {
    setFilePath("");

    if (onRemovePicture && onChangePicture) {
      onChangePicture("");
      onRemovePicture();
    }
  };

  const editFile = () => {
    imgRef.current?.click();
  };

  const renderLabelSection = () => {
    if (!labelText) return;

    return <LabelText>{labelText}</LabelText>;
  };

  const renderButtonSection = () => {
    if (!filePath || disabled) return;

    return (
      <Box>
        <ChangeImageButton onRemove={clearFile} onEdit={editFile} />
      </Box>
    );
  };

  return (
    <Container gap={containerGap}>
      {renderLabelSection()}

      <FileInputBox>
        <InputImage
          disabled={disabled}
          ref={imgRef}
          type="file"
          accept={
            allowedImageExtensions
              ? allowedImageExtensions?.map((extension) => `.${extension}`).join(", ")
              : "image/*"
          }
          id="file-input"
          onChange={handleChangeImage}
        />
        {filePath && <ImageSelected src={filePath} alt="Selected" />}
        {!filePath && (
          <LabelImage htmlFor="file-input">
            <Box sx={{ textAlign: "center" }}>
              <FileInputText variant="h6">{translate("choosePicture")}</FileInputText>
              <FileInputSubText variant="body2">{translate("maxSize2mb")}</FileInputSubText>
              <FileInputSubText variant="body2">{translate("formatsJpgGifPng")}</FileInputSubText>
            </Box>
          </LabelImage>
        )}
      </FileInputBox>

      {renderButtonSection()}
    </Container>
  );
};
